import { useEffect, useMemo, useState, type ComponentType, type CSSProperties } from 'react'
import { Activity, CreditCard, DollarSign, HeartPulse, Route, ShieldCheck, Users } from 'lucide-react'
import { FounderActivityDashboard } from './FounderActivityDashboard'
import { FounderAiSpendDashboard } from './FounderAiSpendDashboard'
import { FounderSystemHealthDashboard } from './FounderSystemHealthDashboard'
import { FounderBillingDashboard } from './FounderBillingDashboard'
import { FounderToursDashboard } from './FounderToursDashboard'
import { FounderLeadsDashboard } from './FounderLeadsDashboard'
import { FounderAccessPanel } from './FounderAccessPanel'
import { ServerApiClient, type FgCapability } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

type Props = { topInsetPx?: number }
type Tab = 'activity' | 'spend' | 'health' | 'billing' | 'tours' | 'leads' | 'access'

/**
 * A tab is either founder-only (`capability: null` — health, billing, spend,
 * activity, and access management itself) or opened by exactly one grantable
 * capability. The server enforces both; this list only decides what a person
 * is offered, so nobody is handed a tab that answers 403.
 */
const TABS: Array<{
  id: Tab
  label: string
  Icon: ComponentType<{ size?: number }>
  capability: FgCapability | null
}> = [
  { id: 'activity', label: 'Activity', Icon: Activity, capability: null },
  { id: 'spend', label: 'AI spend', Icon: DollarSign, capability: null },
  { id: 'health', label: 'System health', Icon: HeartPulse, capability: null },
  { id: 'billing', label: 'Billing', Icon: CreditCard, capability: null },
  { id: 'tours', label: 'Tours', Icon: Route, capability: 'tour_author' },
  { id: 'leads', label: 'Leads', Icon: Users, capability: 'crm' },
  { id: 'access', label: 'Access', Icon: ShieldCheck, capability: null },
]

const tabBtn = (active: boolean): CSSProperties => ({
  border: '1px solid #c8d7d2',
  background: active ? '#15343b' : '#fff',
  color: active ? '#fff' : '#15343b',
  borderRadius: 7,
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
})

const smallText: CSSProperties = { fontSize: 12, color: '#5d6d72', padding: '14px 16px', maxWidth: 1200, margin: '0 auto' }

/** Founder console: a tab switcher over the Activity dashboard and the AI-spend ledger. */
export function FounderConsole({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  // Deep link: a lead-notification URL carries ?fg_crm_contact=<id>. Land on the
  // Leads tab with that contact's detail already open instead of on Activity.
  const [initialContactId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('fg_crm_contact'),
  )
  const [tab, setTab] = useState<Tab>(initialContactId ? 'leads' : 'activity')

  // Who is this, and what may they see? Null until the answer lands — rendering
  // a founder-only panel optimistically would flash data a teammate must not
  // read and fire a request that 403s.
  const [access, setAccess] = useState<{ founder: boolean; capabilities: FgCapability[] } | null>(null)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await ensureApiSessionReady()
        const out = await api.getFounderActivityAccess()
        if (cancelled) return
        setAccess({
          founder: out?.founder === true,
          capabilities: Array.isArray(out?.capabilities) ? out.capabilities : [],
        })
      } catch {
        if (!cancelled) setAccess({ founder: false, capabilities: [] })
      }
    })()
    return () => { cancelled = true }
  }, [api])

  const visibleTabs = useMemo(() => {
    if (!access) return []
    if (access.founder) return TABS
    return TABS.filter((t) => t.capability !== null && access.capabilities.includes(t.capability))
  }, [access])

  // Resolved during render, not in an effect: a stale default or the
  // ?fg_crm_contact deep link must never paint a forbidden panel, not even for
  // one frame.
  const activeTab: Tab | null = visibleTabs.some((t) => t.id === tab)
    ? tab
    : visibleTabs[0]?.id ?? null

  return (
    <div style={{ height: `calc(100vh - ${topInsetPx}px)`, marginTop: topInsetPx, overflowY: 'auto', overflowX: 'hidden' }}>
      <div
        style={{
          padding: '14px 16px 0',
          display: 'flex',
          gap: 8,
          maxWidth: 1200,
          margin: '0 auto',
          width: '100%',
        }}
      >
        {visibleTabs.map(({ id, label, Icon }) => (
          <button key={id} style={tabBtn(activeTab === id)} onClick={() => setTab(id)}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      {!access ? (
        <div style={smallText}>Checking your access…</div>
      ) : activeTab === 'activity' ? (
        <FounderActivityDashboard topInsetPx={0} />
      ) : activeTab === 'spend' ? (
        <FounderAiSpendDashboard topInsetPx={0} />
      ) : activeTab === 'health' ? (
        <FounderSystemHealthDashboard topInsetPx={0} />
      ) : activeTab === 'billing' ? (
        <FounderBillingDashboard topInsetPx={0} />
      ) : activeTab === 'tours' ? (
        <FounderToursDashboard topInsetPx={0} />
      ) : activeTab === 'leads' ? (
        <FounderLeadsDashboard topInsetPx={0} initialContactId={initialContactId} />
      ) : activeTab === 'access' ? (
        <FounderAccessPanel topInsetPx={0} />
      ) : (
        <div style={smallText}>
          This account has no founder console areas. Ask a founder to grant you tour authoring or CRM access.
        </div>
      )}
    </div>
  )
}
